import React, { useCallback, createContext, useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { captureException } from '@sentry/react';

import { useAccessorials } from './utils';
import { applyQboTag, replaceServiceLevelRows, upsertRateClassRate, upsertServiceLevelRate } from './rateUtils';
import { mergeFuelBucket } from './fuelUtils';
import {
    GET_TARIFFS_BY_TARIFF_ID,
    UPSERT_RATES,
    DELETE_TARIFF,
    LIST_TARIFFS_BY_CLIENT_ID,
    QUERY_TARIFF_CLIENT,
    QUERY_TARIFF_RATE_CLASSES,
} from './graphql';
import { DEFAULT_RATES } from './types/default';
import { DEFAULT_RATES as LIVING_SPACES_RATES } from './types/livingSpaces';
import { DEFAULT_RATES as AMERICAN_FREIGHT_RATES } from './types/americanFreight';
import { DEFAULT_RATES as ONWARD_CONSOLIDATED } from './types/onwardConsolidated';
import { DEFAULT_RATES as DRIVER_DAILY_FLAT_RATES } from './driver/dailyFlat';
import { DEFAULT_RATES as DRIVER_DAILY_MILEAGE_RATES } from './driver/dailyMileage';
import { DEFAULT_RATES as WERNER_FLAT_RATES } from './types/wernerDefault';
import { cloneDeep, uniq } from 'lodash';
import { useClientUser } from '@/hooks';
import { useTariffRoutes } from './routes';
import { QUERY_DELIVERY_PROFILES, QUERY_SUBREGIONS_BY_CLIENT } from '@/components/Subregions/graphql/queries';
import { QUERY_ADMIN_TAGS } from '@/components/admin/AdminTags/graphql';
import { QUERY_SERVICE_CATALOG } from '@/components/Account/ServiceCatalog/graphql';

// ONWARD_CONSOLIDATED item pricing method. There is no DB column for this — the method
// is encoded by which rate `type` a tariff carries, and the algo sums whatever is present.
// The editor uses a single selector that keeps these mutually exclusive on save:
//   INCREMENTAL_ITEMS   -> *_ITEMS_INCREMENTAL_RATE (marginal, per-band)
//   TOTAL_ITEMS         -> *_ITEMS_TOTAL_RATE        (whole-count, matched band x total qty)
//   INCREMENTAL_CABINETS-> *_CABINETS_INCREMENTAL_RATE
// Pre-existing "legacy" tariffs carry both items + cabinets and price additively via the
// algo; they're unchanged until edited, then collapse to the selected method on save
// (see item_rate_legacy, which drives an editor warning).
export const ITEM_RATE_MODES = {
    INCREMENTAL_ITEMS: 'INCREMENTAL_ITEMS',
    TOTAL_ITEMS: 'TOTAL_ITEMS',
    INCREMENTAL_CABINETS: 'INCREMENTAL_CABINETS',
};

const ACTIVE_ITEM_SUFFIX = {
    [ITEM_RATE_MODES.INCREMENTAL_ITEMS]: '_ITEMS_INCREMENTAL_RATE',
    [ITEM_RATE_MODES.TOTAL_ITEMS]: '_ITEMS_TOTAL_RATE',
    [ITEM_RATE_MODES.INCREMENTAL_CABINETS]: '_CABINETS_INCREMENTAL_RATE',
};

const ITEM_METHOD_SUFFIXES = Object.values(ACTIVE_ITEM_SUFFIX);

// True for an item-method rate type that isn't the active method for `mode` — excluded from
// save and from required-field validation so a saved tariff resolves to exactly one method.
const isInactiveItemType = (type, mode) =>
    ITEM_METHOD_SUFFIXES.some((suffix) => type.endsWith(suffix)) && !type.endsWith(ACTIVE_ITEM_SUFFIX[mode]);

const isPlainObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

const normalizeRateDimensions = (rate) => {
    const { dimension_key, ...rest } = rate;
    return isPlainObject(rest.dimensions) ? { ...rest, dimensions: { ...rest.dimensions } } : rest;
};

const hasConfiguredRate = (rates, suffix) =>
    Object.entries(rates || {}).some(
        ([type, rows]) => type.endsWith(suffix) && (rows || []).some((r) => Number.isFinite(r.rate) && r.rate > 0)
    );

// Open the editor in the method the loaded tariff was saved with (Total > Items >
// Cabinets), defaulting to Incremental Items for a fresh tariff.
const detectItemRateMode = (rates) => {
    if (hasConfiguredRate(rates, '_ITEMS_TOTAL_RATE')) return ITEM_RATE_MODES.TOTAL_ITEMS;
    if (hasConfiguredRate(rates, '_ITEMS_INCREMENTAL_RATE')) return ITEM_RATE_MODES.INCREMENTAL_ITEMS;
    if (hasConfiguredRate(rates, '_CABINETS_INCREMENTAL_RATE')) return ITEM_RATE_MODES.INCREMENTAL_CABINETS;
    return ITEM_RATE_MODES.INCREMENTAL_ITEMS;
};

// A legacy tariff carries more than one item-method type (e.g. items + cabinets billed
// additively). Saving collapses it to the selected method, so the editor warns first.
const countItemMethods = (rates) => ITEM_METHOD_SUFFIXES.filter((suffix) => hasConfiguredRate(rates, suffix)).length;

export const Context = createContext();

export const ContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const { tariff_id } = useParams();
    const { exitPath } = useTariffRoutes();
    const {
        tags,
        client_id,
        writableTariffClientIds,
        organization_id,
        isOnwardAdmin,
        isCorporateAdmin,
        isCarrierAdmin,
    } = useClientUser();
    const [updates, setUpdates] = useState({});
    const [rates, setRates] = useState({});
    // Ephemeral item-pricing method selection (ONWARD_CONSOLIDATED only). Null until the
    // user picks one; the effective mode is derived from the loaded rates so the editor
    // opens in the saved method and legacy tariffs stay legacy.
    const [itemRateModeOverride, setItemRateMode] = useState(null);

    const { data: tariff, loading: tariffLoading } = useQuery(GET_TARIFFS_BY_TARIFF_ID, {
        variables: {
            tariff_id,
        },
    });

    const { data: overridesData } = useQuery(LIST_TARIFFS_BY_CLIENT_ID, {
        variables: {
            client_ids: writableTariffClientIds,
        },
    });

    const { data: tagsData } = useQuery(QUERY_ADMIN_TAGS, { variables: {} });

    const marketplaceTags = useMemo(() => {
        return [...(tagsData?.results || [])].sort((a, b) => a.tag.localeCompare(b.tag));
    }, [tagsData]);

    const overrideTypes = useMemo(() => {
        return uniq(['DEFAULT', ...(overridesData?.overrides || []).map((po) => po.algo_type)]);
    }, [overridesData]);

    const [upsertRates] = useMutation(UPSERT_RATES, {
        update: (cache) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'pricing_tariffs' });
        },
        onCompleted: () => {
            navigate(exitPath);
        },
        onError: (e) => {
            captureException(e);
        },
    });

    const [deleteTariff] = useMutation(DELETE_TARIFF, {
        variables: {
            tariff_id,
        },
        update: (cache) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'pricing_tariffs' });
        },
        onCompleted: () => {
            navigate(exitPath);
        },
        onError: (e) => {
            captureException(e);
        },
    });

    useEffect(() => {
        if (!client_id) {
            return;
        }
        const valid = writableTariffClientIds;
        if (tariff?.result && !valid.includes(tariff?.result?.client_id)) {
            navigate(exitPath);
        }
    }, [tariff, client_id]);

    const [algoType, tariffType] = useMemo(() => {
        return [tariff?.result?.algo_type, tariff?.result?.tariff_type];
    }, [tariff]);

    const tariffClientId = tariff?.result?.client_id;
    // A tariff is shipper-scoped: it prices exactly its owner-shipper's catalog. Tariffs
    // without a shipper owner (org-level, carrier-owned) have no catalog to price.
    const serviceCatalogWhere = useMemo(() => ({ client_id: { _eq: tariffClientId } }), [tariffClientId]);
    const serviceCatalogReady = algoType === 'ONWARD_CONSOLIDATED' && !!tariffClientId;
    const { data: serviceCatalogData } = useQuery(QUERY_SERVICE_CATALOG, {
        variables: { where: serviceCatalogWhere },
        skip: !serviceCatalogReady,
    });
    const serviceCatalog = useMemo(() => serviceCatalogData?.service_catalog || [], [serviceCatalogData]);

    const { data: rateClassData } = useQuery(QUERY_TARIFF_RATE_CLASSES, {
        variables: { client_id: tariffClientId },
        skip: !serviceCatalogReady,
    });

    // QBO codes and custom service levels belong to the tariff's owner client. No fallback to the logged-in
    // user's lists: offering one company's codes on another's tariff is how the wrong code gets saved, and
    // the tariff's custom-level rates are keyed by the owner's service levels.
    const { data: tariffClientData } = useQuery(QUERY_TARIFF_CLIENT, {
        variables: { client_id: tariffClientId },
        skip: !tariffClientId,
    });
    const qboTags = useMemo(() => tariffClientData?.client?.qbo_tags || [], [tariffClientData]);
    const serviceLevels = useMemo(() => tariffClientData?.client?.service_levels || [], [tariffClientData]);
    // Feeds state.loading so the editor never mounts with the owner's QBO codes or service levels missing,
    // which reads as "this client has none". Org-level tariffs have no owner client, so nothing to wait for.
    const tariffClientReady = !tariffClientId || !!tariffClientData;

    const defaults = useMemo(() => {
        switch (algoType) {
            case 'SHIPPER_GLOBAL_DEFAULT':
            case 'CARRIER_GLOBAL_DEFAULT':
            case 'DEFAULT':
                return DEFAULT_RATES;
            case 'LIVING_SPACES':
                return LIVING_SPACES_RATES;
            case 'ROCKET_SHIPPING':
                return AMERICAN_FREIGHT_RATES;
            case 'ONWARD_CONSOLIDATED':
                return ONWARD_CONSOLIDATED;
            case 'DRIVER_DAILY_FLAT':
                return DRIVER_DAILY_FLAT_RATES;
            case 'DRIVER_DAILY_MILEAGE':
                return DRIVER_DAILY_MILEAGE_RATES;
            case 'WERNER_CARRIER_DEFAULT':
                return WERNER_FLAT_RATES;
            default:
                return {};
        }
    }, [algoType]);

    const [fetchDeliveryProfiles, { data: deliveryProfilesData, loading: deliveryProfilesLoading }] =
        useLazyQuery(QUERY_DELIVERY_PROFILES);

    const [fetchSubregions, { data: subregionsData, loading: subregionsLoading }] =
        useLazyQuery(QUERY_SUBREGIONS_BY_CLIENT);

    useEffect(() => {
        if (algoType !== 'ONWARD_CONSOLIDATED') return;

        if (isOnwardAdmin) {
            fetchDeliveryProfiles({ variables: { query: { organization_id: { _is_null: true } } } });
        } else if (isCorporateAdmin) {
            fetchDeliveryProfiles({ variables: { query: { organization_id: { _eq: organization_id } } } });
        } else if (client_id) {
            fetchSubregions({ variables: { client_id } });
        }
    }, [algoType, client_id, isOnwardAdmin, isCorporateAdmin, organization_id]);

    const subregionOptions = useMemo(() => {
        if (subregionsData?.results?.length > 0) {
            return {
                DEFAULT: {
                    label: 'Default',
                    subregions: subregionsData.results.filter((subregion) => !!subregion.is_pricing_zone),
                },
            };
        }

        if (deliveryProfilesData?.results?.length > 0) {
            return Object.fromEntries(
                deliveryProfilesData.results.map((profile) => {
                    return [
                        profile.delivery_profile_id,
                        {
                            label: profile.name,
                            subregions: profile.subregions.filter((subregion) => !!subregion.is_pricing_zone),
                        },
                    ];
                })
            );
        }

        return {};
    }, [subregionsData, deliveryProfilesData]);

    const accessorials = useAccessorials(algoType, tags);

    const required = useMemo(() => {
        return (tariff?.result?.requirements || []).map((req) => req.rate_type);
    }, [tariff]);

    const old = useMemo(() => {
        return (tariff?.result?.rates || []).reduce((acc, rate) => {
            const next = [...(acc[rate.type] || []), rate];
            acc[rate.type] = next.sort((l, r) => {
                if (l.min2 === r.min2) {
                    return l.min - r.min;
                }
                return l.min2 - r.min2;
            });

            return acc;
        }, {});
    }, [tariff]);

    const updated = useMemo(() => {
        return {
            name: updates.name || tariff?.result?.name,
            base_rates_axis: updates.base_rates_axis || tariff?.result?.base_rates_axis,
            base_rates_axis2:
                updates.base_rates_axis2 || updates.base_rates_axis2 === null
                    ? updates.base_rates_axis2
                    : tariff?.result?.base_rates_axis2,
            base_rates_qty: updates.base_rates_qty || tariff?.result?.base_rates_qty,
            fsc_mode: updates.fsc_mode || tariff?.result?.fsc_mode || 'MODIFIER',
            allow_zero_base_rate: updates.allow_zero_base_rate ?? tariff?.result?.allow_zero_base_rate ?? false,
        };
    }, [tariff, updates]);

    // Effective item-pricing method: user override if set, otherwise detected from the
    // loaded rates (defaults to Incremental Items for pre-existing and fresh tariffs).
    const itemRateMode = useMemo(() => itemRateModeOverride ?? detectItemRateMode(old), [itemRateModeOverride, old]);

    // True when the loaded tariff carried more than one item-method type (legacy additive
    // items + cabinets). Drives the editor warning that saving collapses to one method.
    const itemRateLegacy = useMemo(
        () => algoType === 'ONWARD_CONSOLIDATED' && countItemMethods(old) > 1,
        [algoType, old]
    );

    const missing = useMemo(() => {
        // Drop item-method requirements that aren't active for the selected mode, so an
        // empty inactive grid (e.g. ITEMS_INCREMENTAL_RATE while pricing by TOTAL_ITEMS)
        // doesn't block save. Item-rate types live in pricing_override_requirements;
        // total/cabinet types are optional.
        const isInactive = (type) => algoType === 'ONWARD_CONSOLIDATED' && isInactiveItemType(type, itemRateMode);
        // Every catalog service must carry a flat SERVICE_RATE before a v2 tariff can save. Keyed
        // SERVICE_RATE:<code> so the Services section can flag the specific missing row, and so the
        // Save button (disabled on Object.keys(missing).length) blocks until all are set.
        const serviceMissing =
            algoType === 'ONWARD_CONSOLIDATED'
                ? serviceCatalog.filter((svc) => {
                      const row = (rates.SERVICE_RATE || []).find((r) => r?.dimensions?.code === svc.service_code);
                      return !(row && Number.isFinite(row.rate) && row.rate >= 0);
                  })
                : [];
        return {
            ...Object.fromEntries(
                required
                    .filter((type) => !isInactive(type))
                    .filter((type) => {
                        return !(rates[type] || []).every((bucket) => bucket?.rate !== null && bucket?.rate >= 0);
                    })
                    .map((attr) => [attr, true])
            ),
            ...Object.fromEntries(serviceMissing.map((svc) => [`SERVICE_RATE:${svc.service_code}`, true])),
        };
    }, [algoType, rates, required, updated, itemRateMode, serviceCatalog]);

    // Catalog classes plus any class already priced on this tariff, so a class removed from the
    // catalog stays visible until its rows are cleared.
    const rateClasses = useMemo(() => {
        const fromCatalog = (rateClassData?.item_dimensions || []).map((row) => row.rate_class);
        const fromRates = Object.entries(rates || {})
            .filter(([type]) => type.endsWith('_ITEM_RATE'))
            .flatMap(([, rows]) => (rows || []).map((row) => row?.dimensions?.rate_class));
        return uniq([...fromCatalog, ...fromRates].map((c) => (c || '').trim().toUpperCase()).filter(Boolean)).sort();
    }, [rateClassData, rates]);

    useEffect(() => {
        const keys = new Set([...required, ...Object.keys(defaults), ...Object.keys(old)]);
        setRates(
            [...keys].reduce((acc, type) => {
                const fromDefault = defaults[type] || [];
                const fromOld = cloneDeep(old[type] || []);
                return {
                    ...acc,
                    [type]: fromOld.length > 0 ? fromOld : fromDefault,
                };
            }, {})
        );
    }, [old, defaults, required]);

    const addFuelRate = useCallback(
        ({ type = 'FUEL_PRICE_MODIFIER' } = {}) => {
            setRates((prev) => {
                const next = rates?.[type] || [];
                next[next.length - 1] = {
                    ...next[next.length - 1],
                    max: null,
                };

                return {
                    ...prev,
                    [type]: [
                        ...next,
                        {
                            min: 0,
                            max: 'infinity',
                            rate: null,
                        },
                    ],
                };
            });
        },
        [rates]
    );

    const setFuelRate = useCallback(
        ({ bucket, rate, max = null, type = 'FUEL_PRICE_MODIFIER' }) => {
            setRates((prev) => {
                const next = [...(rates?.[type] || [])];
                next[bucket] = mergeFuelBucket(next?.[bucket], { rate, max });

                if (max !== null) {
                    next[bucket + 1] = {
                        ...(next?.[bucket + 1] || {}),
                        min: max,
                    };
                }

                return {
                    ...prev,
                    [type]: next,
                };
            });
        },
        [rates]
    );

    // Sets a single-row rate type (used by FUEL_PRICE_FLAT, FUEL_PRICE_MPG modes — each
    // mode has exactly one config row). Preserves existing qbo_tag if not provided.
    const setSingleRate = useCallback(({ type, rate, qbo_tag }) => {
        setRates((prev) => {
            const existing = prev[type]?.[0] || { min: 0, max: 'infinity', qbo_tag: '' };
            return {
                ...prev,
                [type]: [
                    {
                        ...existing,
                        rate: rate !== undefined ? rate : existing.rate,
                        qbo_tag: qbo_tag !== undefined ? qbo_tag : existing.qbo_tag,
                    },
                ],
            };
        });
    }, []);

    const setAllFuelRates = useCallback((newRates, { type = 'FUEL_PRICE_MODIFIER' } = {}) => {
        setRates((prev) => {
            return {
                ...prev,
                [type]: newRates.map(
                    ({ min, max, rate, bucket }) => (
                        bucket,
                        {
                            min,
                            max,
                            rate,
                        }
                    )
                ),
            };
        });
    }, []);

    const deleteFuelRate = useCallback(
        ({ bucket, type = 'FUEL_PRICE_MODIFIER' }) => {
            setRates((prev) => {
                const next = [...(rates?.[type] || [])];
                const rem = next.splice(bucket, 1);

                next[next.length - 1] = {
                    ...(next?.[next.length - 1] || {}),
                    max: rem[0].max,
                };

                return {
                    ...prev,
                    [type]: next,
                };
            });
        },
        [rates]
    );

    const updateQBO = useCallback(
        ({ qbo, types, service_level_id }) => {
            setRates((prev) => {
                const updated = Object.fromEntries(
                    types.map((type) => {
                        return [type, applyQboTag(prev[type] || [], { qbo, service_level_id })];
                    })
                );

                return {
                    ...prev,
                    ...updated,
                };
            });
        },
        [rates]
    );

    return (
        <Context.Provider
            value={{
                state: {
                    loading: tariffLoading || !tariffClientReady || subregionsLoading || deliveryProfilesLoading,
                    algoType,
                    tariffType,
                    accessorials,
                    rates,
                    subregionOptions,
                    overrideTypes,
                    marketplaceTags,
                    serviceCatalog,
                    rateClasses,
                    qbo_tags: qboTags,
                    service_levels: serviceLevels,
                    isCarrierAdmin,
                    item_rate_mode: itemRateMode,
                    item_rate_legacy: itemRateLegacy,
                    ...updated,
                },
                callbacks: {
                    updateTariff: (patch) => setUpdates((prev) => ({ ...prev, ...patch })),
                    setItemRateMode,
                    delete: () => {
                        deleteTariff();
                    },
                    save: () => {
                        // Persist only the active item-method's type so the tariff resolves
                        // to one billing method. UPSERT_RATES delete+replaces, so omitting the
                        // inactive item types drops them. Non-consolidated algos skip this.
                        const isInactive = (type) =>
                            algoType === 'ONWARD_CONSOLIDATED' && isInactiveItemType(type, itemRateMode);
                        upsertRates({
                            variables: {
                                update: {
                                    ...updates,
                                    allow_zero_base_rate: updated.allow_zero_base_rate,
                                },
                                tariff_id,
                                rates: Object.entries(rates).reduce((acc, [type, buckets]) => {
                                    if (isInactive(type)) {
                                        return acc;
                                    }
                                    acc = [
                                        ...acc,
                                        ...buckets
                                            .filter((bucket) => Number.isFinite(bucket.rate))
                                            .map((bucket) => {
                                                const { __typename, ...remaining } = bucket;
                                                return normalizeRateDimensions({
                                                    tariff_id,
                                                    ...remaining,
                                                    type,
                                                });
                                            }),
                                    ];
                                    return acc;
                                }, []),
                            },
                        });
                    },
                    addFuelRate,
                    setFuelRate,
                    setAllFuelRates,
                    setSingleRate,
                    deleteFuelRate,
                    // Custom levels share each CUSTOM_SERVICE_LEVEL_* type, so those are replaced per level: every
                    // level in `serviceLevelIds` or in the new rows. Other types are replaced whole.
                    setRatesMulti: ({ rates, serviceLevelIds }) => {
                        setRates((prev) => {
                            const updates = Object.entries(rates).reduce((acc, [type, rates]) => {
                                let next = rates;
                                if (type.startsWith('CUSTOM_SERVICE_LEVEL')) {
                                    next = replaceServiceLevelRows(prev[type], next, serviceLevelIds);
                                }
                                return {
                                    ...acc,
                                    [type]: next,
                                };
                            }, {});

                            return {
                                ...prev,
                                ...updates,
                            };
                        });
                    },
                    setRates: ({ type, buckets }) => {
                        setRates((prev) => {
                            return {
                                ...prev,
                                [type]: buckets,
                            };
                        });
                    },
                    setRate: ({ type, bucket, rate, min }) => {
                        setRates((prev) => {
                            const clone = cloneDeep(prev?.[type] || []);
                            clone[bucket] = {
                                ...(clone?.[bucket] || { min: 0, max: 'infinity' }),
                                ...(rate || rate === 0 ? { rate } : {}),
                                ...(min || min === 0 ? { min } : {}),
                            };

                            return {
                                ...prev,
                                [type]: clone,
                            };
                        });
                    },
                    // Upsert a single flat SERVICE_RATE row, keyed by service code (dimensions.code).
                    // One row per catalog service; the rates unique index includes dimension_key, so
                    // distinct codes don't collide. Save serializes it like any other rate row.
                    setServiceRate: ({ code, rate, qbo_tag }) => {
                        setRates((prev) => {
                            const clone = cloneDeep(prev?.SERVICE_RATE || []);
                            const idx = clone.findIndex((r) => (r?.dimensions?.code || '') === code);
                            // Coerce to a number so an empty field clears to null and Number.isFinite
                            // (save filter + validation) treats it consistently.
                            const coercedRate =
                                rate === '' || rate === null || rate === undefined ? null : Number(rate);
                            const patch = {
                                ...(rate !== undefined ? { rate: coercedRate } : {}),
                                ...(qbo_tag !== undefined ? { qbo_tag } : {}),
                            };
                            if (idx >= 0) {
                                clone[idx] = { ...clone[idx], ...patch };
                            } else {
                                clone.push({
                                    type: 'SERVICE_RATE',
                                    min: 0,
                                    max: 'infinity',
                                    qbo_tag: '',
                                    rate: null,
                                    ...patch,
                                    dimensions: { code },
                                });
                            }
                            return { ...prev, SERVICE_RATE: clone };
                        });
                    },
                    updateRate: ({ type, min, min2, subregion_id, rate, service_level_id }) => {
                        setRates((prev) => {
                            const clone = cloneDeep(prev?.[type] || []);
                            const match =
                                clone.find(
                                    (r) =>
                                        r.min === min &&
                                        (min2 >= 0 ? r.min2 === min2 : true) &&
                                        (!!subregion_id ? r.subregion_id === subregion_id : true) &&
                                        (!!service_level_id ? r.service_level_id === service_level_id : true)
                                ) || {};
                            match.rate = rate;

                            return {
                                ...prev,
                                [type]: clone,
                            };
                        });
                    },
                    updateQBO,
                    setRateClassRate: ({ type, ...cell }) => {
                        setRates((prev) => ({ ...prev, [type]: upsertRateClassRate(prev?.[type] || [], cell) }));
                    },
                    setServiceLevelRate: ({ type, ...patch }) => {
                        setRates((prev) => ({ ...prev, [type]: upsertServiceLevelRate(prev?.[type] || [], patch) }));
                    },
                },
                errors: {
                    missing,
                },
            }}
        >
            {children}
        </Context.Provider>
    );
};
